import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem("session_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle the exchange first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const applyAuth = useCallback((token, u) => {
    localStorage.setItem("session_token", token);
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data.session_token, data.user);
    return data.user;
  }, [applyAuth]);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    applyAuth(data.session_token, data.user);
    return data.user;
  }, [applyAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed (clearing session anyway):", e);
    }
    localStorage.removeItem("session_token");
    setUser(null);
  }, []);

  const updateNotifications = useCallback(async (payload) => {
    const { data } = await api.patch("/profile/notifications", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, applyAuth, checkAuth, updateNotifications }),
    [user, loading, login, register, logout, applyAuth, checkAuth, updateNotifications]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
